import React, { useState, useRef, useEffect } from 'react';
import useSWR from 'swr';
import jsPDF from 'jspdf';
import "jspdf-autotable";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  LinearProgress,
  Alert,
  IconButton,
  Button,
  Tooltip,
  Container,
  CardHeader,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Search as SearchIcon,
  CloudDownload as CloudDownloadIcon,
  CalendarToday as CalendarIcon,
  PictureAsPdf as PictureAsPdfIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { exportStyledExcel } from '../../../utils/exportHelpers';
import { apiHost } from '../../../utils/apiHost';
import { TAMPIL_STATUS, KOLOM_TOTAL, rentangBulan, teksSel, ambilKehadiranCabang } from '../../../utils/rekapPresensi';

// Styled components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  border: '1px solid #ddd',
  padding: '4px 8px',
  fontSize: '12px',
  textAlign: 'center',
  minWidth: '30px',
  maxWidth: '30px',
}));

const StyledHeaderCell = styled(TableCell)(({ theme }) => ({
  border: '1px solid #ddd',
  padding: '4px 8px',
  fontSize: '11px',
  fontWeight: 'bold',
  backgroundColor: '#f5f5f5',
  textAlign: 'center',
  minWidth: '30px',
  maxWidth: '30px',
}));

const NameCell = styled(TableCell)(({ theme }) => ({
  border: '1px solid #ddd',
  padding: '4px 8px',
  fontSize: '12px',
  minWidth: '200px',
  textAlign: 'left',
}));

const NumberCell = styled(TableCell)(({ theme }) => ({
  border: '1px solid #ddd',
  padding: '4px 8px',
  fontSize: '12px',
  minWidth: '40px',
  textAlign: 'center',
}));

axios.defaults.withCredentials = true;

const fetcher = (url) => axios.get(url, { withCredentials: true })
  .then(res => res.data);

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === "https:" ? "https" : "http";
  const baseUrl = apiHost().replace(/^https?:\/\//, "");
  return `${protocol}://${baseUrl}`;
};

export const RekapPresensi = () => {
  // State untuk input bulan dan tahun di form
  const [inputBulan, setInputBulan] = useState(new Date().getMonth() + 1);
  const [inputTahun, setInputTahun] = useState(new Date().getFullYear());
  const [searchTerm, setSearchTerm] = useState('');

  // State untuk memicu fetch data
  const [fetchData, setFetchData] = useState(false);
  const [bulan, setBulan] = useState(new Date().getMonth() + 1);
  const [tahun, setTahun] = useState(new Date().getFullYear());

  // Status tiap hari dihitung server (jadwal shift, absen, izin/sakit/cuti disetujui, libur): satu permintaan per cabang.
  // Admin hanya melihat cabangnya sendiri (server mengabaikan ?cabang=), superadmin semua cabang.
  const ambilRekap = (bln, thn) => {
    const { dari, sampai } = rentangBulan(thn, bln);
    return ambilKehadiranCabang(axios, getApiBaseUrl(), dari, sampai);
  };

  const { data: rekapBaris, error, isLoading, mutate } = useSWR(
    fetchData ? `rekap-kehadiran:${bulan}:${tahun}` : null,
    () => ambilRekap(bulan, tahun),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  // Fungsi yang dipanggil saat tombol "Cari" ditekan
  const handleSearch = () => {
    setBulan(inputBulan);
    setTahun(inputTahun);
    setFetchData(true);
    // Force revalidate data
    mutate();
  };

  // Initialize fetch on component mount
  useEffect(() => {
    setFetchData(true);
  }, []);

  // Generate days in month
  const getDaysInMonth = (month, year) => {
    return new Date(year, month, 0).getDate();
  };

  const daysInMonth = getDaysInMonth(bulan, tahun);
  const daysArray = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  // Filter data based on search term (username, nama, cabang)
  const cari = searchTerm.toLowerCase();
  const filteredData = (rekapBaris || []).filter((d) =>
    d.username.toLowerCase().includes(cari) || d.nama.toLowerCase().includes(cari) || d.cabang.toLowerCase().includes(cari)
  );
  const simbolHari = (data, day) => TAMPIL_STATUS[data.hari[day]?.status]?.simbol || '';

  // Export to PDF
  const exportToPDF = () => {
    const doc = new jsPDF('l', 'mm', 'a4');
    
    // Title
    doc.setFontSize(16);
    doc.text('REKAP PRESENSI KEHADIRAN', 20, 20);
    
    // Subtitle with month and year
    doc.setFontSize(12);
    doc.text(
      `Periode: ${new Date(tahun, bulan - 1).toLocaleDateString('id-ID', { 
        month: 'long', 
        year: 'numeric' 
      })}`, 
      20, 
      30
    );

    // Prepare table data
    const tableData = [];
    const headers = ['NO', 'NAMA', ...daysArray.map(d => d.toString()), ...KOLOM_TOTAL.map(k => k[1])];

    filteredData.forEach((data, index) => {
      const row = [
        index + 1,
        `${data.username} (${data.cabang})`,
        ...daysArray.map(day => simbolHari(data, day)),
        ...KOLOM_TOTAL.map(k => data.total[k[0]]),
      ];
      tableData.push(row);
    });

    doc.autoTable({
      head: [headers],
      body: tableData,
      startY: 40,
      styles: {
        fontSize: 8,
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [63, 81, 181],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
      },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      columnStyles: {
        0: { cellWidth: 15 }, // NO
        1: { cellWidth: 60 }, // NAMA
        ...Object.fromEntries(KOLOM_TOTAL.map((_, i) => [daysArray.length + 2 + i, { cellWidth: 10 }])),
      }
    });

    doc.save(`rekap-presensi-${bulan}-${tahun}.pdf`);
  };

  const exportToExcel = async () => {
    const headers = ['No', 'Nama', ...daysArray.map(d => d.toString()), ...KOLOM_TOTAL.map(k => k[1])];
    const rows = filteredData.map((data, index) => [
      index + 1,
      `${data.username} (${data.cabang})`,
      ...daysArray.map(day => simbolHari(data, day)),
      ...KOLOM_TOTAL.map(k => data.total[k[0]]),
    ]);

    await exportStyledExcel({
      fileName: `rekap-presensi-${bulan}-${tahun}.xlsx`,
      sheetName: 'Rekap Presensi',
      title: 'Rekap Presensi Kehadiran',
      metaRows: [['Periode', new Date(tahun, bulan - 1).toLocaleDateString('id-ID', { month: 'long', year: 'numeric' })]],
      headers,
      rows,
      columnWidths: [6, 28, ...daysArray.map(() => 4), ...KOLOM_TOTAL.map(() => 5)],
    });
  };

  // Loading state
  if (isLoading) {
    return (
      <Container maxWidth="xl">
        <Card>
          <CardContent>
            <LinearProgress />
            <Typography variant="body2" align="center" sx={{ mt: 2 }}>
              Memuat data presensi...
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ mb: 2 }}>
          Error loading data: {error.message}
        </Alert>
        <Button variant="contained" onClick={() => mutate()}>
          Coba Lagi
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Card>
        <CardHeader
          title={
            <Box display="flex" alignItems="center" gap={1}>
              <CalendarIcon />
              <Typography variant="h6">
                Rekap Presensi Kehadiran - {new Date(tahun, bulan - 1).toLocaleDateString('id-ID', { 
                  month: 'long', 
                  year: 'numeric' 
                })}
              </Typography>
            </Box>
          }
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<PictureAsPdfIcon />}
                onClick={exportToPDF}
                color="primary"
                disabled={filteredData.length === 0}
              >
                Export PDF
              </Button>
              <Button
                variant="contained"
                startIcon={<CloudDownloadIcon />}
                onClick={exportToExcel}
                color="success"
                disabled={filteredData.length === 0}
              >
                Export Excel
              </Button>
            </Box>
          }
        />
        <CardContent>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Bulan</InputLabel>
                <Select
                  value={inputBulan}
                  label="Bulan"
                  onChange={(e) => setInputBulan(e.target.value)}
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <MenuItem key={i + 1} value={i + 1}>
                      {new Date(2024, i).toLocaleDateString('id-ID', { month: 'long' })}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Tahun</InputLabel>
                <Select
                  value={inputTahun}
                  label="Tahun"
                  onChange={(e) => setInputTahun(e.target.value)}
                >
                  {[2023, 2024, 2025, 2026].map(year => (
                    <MenuItem key={year} value={year}>{year}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Cari nama atau cabang..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                disabled={isLoading}
              >
                {isLoading ? 'Memuat...' : 'Cari Data'}
              </Button>
            </Grid>
          </Grid>

          {/* Data Summary */}
          {filteredData.length > 0 && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
              <Typography variant="body2" color="info.contrastText">
                Menampilkan {filteredData.length} karyawan untuk periode {' '}
                {new Date(tahun, bulan - 1).toLocaleDateString('id-ID', { 
                  month: 'long', 
                  year: 'numeric' 
                })}
              </Typography>
            </Box>
          )}

          <TableContainer component={Paper} sx={{ maxHeight: '70vh', overflow: 'auto', mt: 3 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <StyledHeaderCell rowSpan={2}>NO</StyledHeaderCell>
                  <StyledHeaderCell rowSpan={2} sx={{ minWidth: '200px' }}>NAMA</StyledHeaderCell>
                  <StyledHeaderCell colSpan={daysInMonth}>TANGGAL</StyledHeaderCell>
                  {KOLOM_TOTAL.map(([kunci, judul, ket]) => (
                    <StyledHeaderCell key={kunci} rowSpan={2} title={ket}>{judul}</StyledHeaderCell>
                  ))}
                </TableRow>
                <TableRow>
                  {daysArray.map(day => (
                    <StyledHeaderCell key={day}>{day}</StyledHeaderCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.length === 0 ? (
                  <TableRow>
                    <StyledTableCell colSpan={daysInMonth + 2 + KOLOM_TOTAL.length}>
                      <Typography variant="body2" color="text.secondary" align="center">
                        {isLoading ? 'Memuat data...' : 'Tidak ada data presensi untuk periode ini'}
                      </Typography>
                    </StyledTableCell>
                  </TableRow>
                ) : (
                  filteredData.map((data, index) => (
                    <TableRow key={data.userId}>
                      <NumberCell>{index + 1}</NumberCell>
                      <NameCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {data.nama}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {data.username} · {data.cabang}
                          </Typography>
                        </Box>
                      </NameCell>
                      {daysArray.map(day => {
                        const h = data.hari[day];
                        const t = TAMPIL_STATUS[h?.status];
                        const lewatBatas = h && (h.telatMenit > 0 || h.pulangCepatMenit > 0);
                        return (
                          <StyledTableCell key={day} sx={lewatBatas ? { backgroundColor: '#fff8e1' } : undefined}>
                            {t ? (
                              <Tooltip title={teksSel(h)} arrow>
                                <Box sx={{ color: t.warna, fontWeight: 'bold', cursor: 'default' }}>{t.simbol}</Box>
                              </Tooltip>
                            ) : ''}
                          </StyledTableCell>
                        );
                      })}
                      {KOLOM_TOTAL.map(([kunci]) => (
                        <StyledTableCell key={kunci} sx={{ fontWeight: 'bold', color: data.total[kunci] > 0 ? (TAMPIL_STATUS[kunci]?.warna || '#ef6c00') : 'text.disabled' }}>
                          {data.total[kunci]}
                        </StyledTableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              Keterangan:
            </Typography>
            <Box display="flex" gap={3} flexWrap="wrap">
              {Object.entries(TAMPIL_STATUS).map(([kunci, t]) => (
                <Box key={kunci} display="flex" alignItems="center" gap={1}>
                  <Box sx={{ color: t.warna, fontWeight: 'bold' }}>{t.simbol}</Box>
                  <Typography variant="caption">{t.label}</Typography>
                </Box>
              ))}
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Kolom kanan: {KOLOM_TOTAL.map(([, judul, ket]) => `${judul} = ${ket}`).join(', ')}. Sel berlatar kuning = terlambat atau pulang cepat (arahkan kursor untuk rinciannya).
              Sel kosong = belum ada absen atau karyawan belum punya jadwal. Status dihitung sesuai Pengaturan Absensi cabang.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};